import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import Banner from "./components/Banner";
import Navbar from "./components/Navbar";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import Card from "./components/Card";
import Margin from "./components/Margin";
import NumericInput from "./components/NumericInput";
import Tabs from "./components/Tabs";
import { logEvent } from "firebase/analytics";
import { initializeAnalytics } from "./firebase";
import Chart from "./components/Chart";

const theme = createTheme({
  typography: {
    fontFamily: "Nunito, Arial",
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Nunito';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: local('Nunito Regular'), local('Nunito-Regular'), 
          url(https://fonts.gstatic.com/s/nunito/v11/XRXV3I6Li01BKofINeaBTMnFcQ.woff2) 
          format('woff2');
          unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, 
            U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, 
            U+2215, U+FEFF, U+FFFD;
        }
      `,
    },
  },
});

interface SingleResult {
  actual: { cotization: number; contribution: number };
  boric: { cotization: number; contribution: number };
  parisi: { cotization: number; contribution: number };
  difference: number;
}

export interface Results {
  first: SingleResult;
  fifth: SingleResult;
  tenth: SingleResult;
}

function App() {
  const analytics = initializeAnalytics();
  const endRef = useRef<null | HTMLDivElement>(null);
  const [value, setValue] = useState<number>();
  const [show, setShow] = useState(false);
  const [results, setResults] = useState<Results>({
    first: {
      actual: { cotization: 0, contribution: 0 },
      boric: { cotization: 0, contribution: 0 },
      parisi: { cotization: 0, contribution: 0 },
      difference: 0,
    },
    fifth: {
      actual: { cotization: 0, contribution: 0 },
      boric: { cotization: 0, contribution: 0 },
      parisi: { cotization: 0, contribution: 0 },
      difference: 0,
    },
    tenth: {
      actual: { cotization: 0, contribution: 0 },
      boric: { cotization: 0, contribution: 0 },
      parisi: { cotization: 0, contribution: 0 },
      difference: 0,
    },
  });

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // useEffect(() => {
  //   logEvent(analytics, "screen_view", {
  //     firebase_screen: "inicio",
  //     firebase_screen_class: "inicio",
  //   });
  // }, []);

  const handleClick = () => {
    if (!!value && !isNaN(value)) {
      const yearValue = Math.abs(value * 12);
      const firstYearActualCotization = Math.round(yearValue * 0.1);
      const firstYearActualContribution = Math.round(yearValue * 0.1 * 1.04);
      const firstYearBoricCotization = Math.round(yearValue * 0.16);
      const firstYearBoricContribution = Math.round(yearValue * 0.12 * 1.04);
      const firstYearParisiCotization = Math.round(yearValue * 0.16);
      const firstYearParisiContribution = Math.round(
        yearValue * 0.16 * (1 + (4 / 100) * 0.97 + (3 / 100) * (3 / 100))
      );
      logEvent(analytics, "calcular", {
        value: value,
      });
      setResults({
        first: {
          actual: {
            cotization: firstYearActualCotization,
            contribution: firstYearActualContribution,
          },
          boric: {
            cotization: firstYearBoricCotization,
            contribution: firstYearBoricContribution,
          },
          parisi: {
            cotization: firstYearParisiCotization,
            contribution: firstYearParisiContribution,
          },
          difference: firstYearParisiContribution - firstYearBoricContribution,
        },
        fifth: {
          actual: {
            cotization: firstYearActualCotization * 5,
            contribution: firstYearActualContribution * 5,
          },
          boric: {
            cotization: firstYearBoricCotization * 5,
            contribution: firstYearBoricContribution * 5,
          },
          parisi: {
            cotization: firstYearParisiCotization * 5,
            contribution: firstYearParisiContribution * 5,
          },
          difference:
            firstYearParisiContribution * 5 - firstYearBoricContribution * 5,
        },
        tenth: {
          actual: {
            cotization: firstYearActualCotization * 10,
            contribution: firstYearActualContribution * 10,
          },
          boric: {
            cotization: firstYearBoricCotization * 10,
            contribution: firstYearBoricContribution * 10,
          },
          parisi: {
            cotization: firstYearParisiCotization * 10,
            contribution: firstYearParisiContribution * 10,
          },
          difference:
            firstYearParisiContribution * 10 - firstYearBoricContribution * 10,
        },
      });
      setShow(true);
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
  };

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <Navbar>
          <Banner />
          <Box sx={{ padding: 3 }}>
            <Divider>
              <Chip
                label="Inicio"
                size="small"
                color="primary"
                sx={{ backgroundColor: "#56a0e8" }}
              />
            </Divider>
            <Box sx={{display:'flex', flexDirection:'column', alignItems:'center'}}>
              <Margin>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ fontFamily: "Nunito" }}
                >
                  Cuanto le pagarás
                </Typography>
                <Typography variant="h5" style={{ fontFamily: "Nunito" }}>
                  extra a Boric-Marcel con la nueva reforma
                </Typography>
              </Margin>
              <Margin>
                <Card
                  title="¿De qué trata la reforma?"
                  content="La reforma de Marcel y Boric no es más que otro intento por meterle la mano al bolsillo al sueldo de millones de trabajadores de Chile. Cuando la prioridad no es la gente nunca les alcanzará la plata y siempre querrán más."
                />
              </Margin>
            </Box>
            <Divider>
              <Chip
                label="Calculadora"
                size="small"
                color="primary"
                sx={{ backgroundColor: "#56a0e8" }}
              />
            </Divider>
            <Box sx={{display:'flex', flexDirection:'column', alignItems:'center'}}>
              <Margin>
                <Typography variant="h5" style={{ fontFamily: "Nunito" }}>
                  Ingresá tu sueldo bruto
                </Typography>
              </Margin>
              <FormControl fullWidth sx={{margin:2, alignItems:'center', maxWidth:600}}>
                <NumericInput
                  value={value}
                  onChange={(e) => setValue(parseInt(e.target.value))}
                />
              </FormControl>
              <Button sx={{maxWidth:600}}
                variant="contained"
                disabled={!value}
                fullWidth
                onClick={handleClick}
              >
                Calcular
              </Button>
            </Box>
            <div ref={endRef}></div>
            {show && (
              <Margin>
                <Margin>
                  <Divider>
                    <Chip
                      label="Resultado"
                      size="small"
                      color="primary"
                      sx={{ backgroundColor: "#56a0e8" }}
                    />
                  </Divider>
                </Margin>

                <Tabs results={results} />
                <Divider>
                  <Margin>
                    <Chip
                      label="Gráfico"
                      size="small"
                      color="primary"
                      sx={{ backgroundColor: "#56a0e8" }}
                    />
                  </Margin>
                </Divider>
                <Typography variant="h5" style={{ fontFamily: "Nunito" }}>
                  Gráfico de aporte a cuenta a través de los años
                </Typography>
                <Chart results={results} />
              </Margin>
            )}
          </Box>
          <Box sx={{width:'100%', height:30, backgroundColor:'#56a0e8', marginTop:7}}/>
          {/* <Banner /> */}
        </Navbar>
      </ThemeProvider>
    </div>
  );
}

export default App;
